import { useState, useEffect, useCallback } from "react";
import { api, ApiError } from "@/lib/api-client";

export interface HeldGroup {
  id: string;
  status: string;
  trigger: string;
  docCount: number;
  createdAt: string;
  flushedAt: string | null;
  destinationName: string;
  destinationActive: boolean;
  jobTypeName: string;
  batchLabel: string | null;
  standingHardBalance: boolean;
}

export function usePendingRelease() {
  const [groups, setGroups] = useState<HeldGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<{ groups?: HeldGroup[] }>(
        "/export-groups?status=held_manual",
      );
      // Defensive: a transitional/empty payload without `groups` must not
      // make `groups` undefined (consumers read `.length`).
      setGroups(data.groups ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load pending groups");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const release = useCallback(
    async (id: string): Promise<void> => {
      try {
        await api.post(`/export-groups/${id}/release`);
      } catch (e) {
        // Propagate 400 error message to the caller for a toast
        if (e instanceof ApiError) {
          const body = e.body as { error?: string } | null;
          throw new Error(body?.error ?? `Release failed (${e.status})`);
        }
        throw e;
      }
      await load();
    },
    [load],
  );

  return { groups, loading, error, refetch: load, release };
}
